import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, output } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AnatomicalSideEnum, BoneTypeEnum, BoneViewEnum, ImageOrientationEnum } from '../../core';
import { ImageCategorizationForm } from '../../models';
import { CanvasService } from '../../services';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe } from '../../shared';


/**
 * Component for image orientation on image garage modal.
 */
@Component({
	selector: 'image-orientation',
	templateUrl: './image-orientation.component.html',
	imports: [
		NgbTooltip,
		BoneTypeLabelPipe,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ImageOrientationComponent extends BaseComponent implements OnInit {

	private readonly canvasSrv = inject(CanvasService);

	form = input<ImageCategorizationForm>();
	changed = output<ImageOrientationEnum | null>();
	isCorrect = model<boolean | null>(null);

	isFulllegVisible = input(false);
	isFullLeg = model<boolean>(false);

	isValidationForced = input(false);
	isResetAnalysisWarning = input(false);

	orientLeftLabel = computed(() => this.canvasSrv.leftLabel());
	orientRightLabel = computed(() => this.canvasSrv.rightLabel());
	orientDownLabel = computed(() => this.canvasSrv.bottomLabel());
	orientUpLabel = computed(() => this.canvasSrv.topLabel());

	imgOrientationSrc = '';

	ngOnInit(): void {
		this.imgOrientationSrc = this._imgOrientationSrc;
	}

	private get _imgOrientationSrc(): string {
		return this.form()!.boneSide == AnatomicalSideEnum.Right ? this._imgOrientationSrc_Right : this._imgOrientationSrc_Left;
	}

	private get _imgOrientationSrc_Right(): string {
		if (this.form()!.boneView == BoneViewEnum.AP) {
			switch (this.form()!.boneType) {
				case BoneTypeEnum.LongLeg: return BoneImageEnum.Right_FullLeg_AP;
				case BoneTypeEnum.Femur: return BoneImageEnum.Right_Femur_AP;
				case BoneTypeEnum.Tibia: return BoneImageEnum.Right_Tibia_AP;
				case BoneTypeEnum.Ankle: return BoneImageEnum.Right_Ankle_AP;
				case BoneTypeEnum.Forefoot: return BoneImageEnum.Right_Forefoot_AP;
				case BoneTypeEnum.Hindfoot: return BoneImageEnum.Right_Hindfoot_AP;
				default: return '';
			}
		} else {
			switch (this.form()!.boneType) {
				case BoneTypeEnum.LongLeg: return BoneImageEnum.Right_FullLeg_LT;
				case BoneTypeEnum.Femur: return BoneImageEnum.Right_Femur_LT;
				case BoneTypeEnum.Tibia: return BoneImageEnum.Right_Tibia_LT;
				case BoneTypeEnum.Ankle: return BoneImageEnum.Right_Ankle_LT;
				case BoneTypeEnum.Forefoot: return BoneImageEnum.Right_Forefoot_LT;
				case BoneTypeEnum.Hindfoot: return BoneImageEnum.Right_Hindfoot_LT;
				default: return '';
			}
		}
	}

	private get _imgOrientationSrc_Left(): string {
		if (this.form()!.boneView == BoneViewEnum.AP) {
			switch (this.form()!.boneType) {
				case BoneTypeEnum.LongLeg: return BoneImageEnum.Left_FullLeg_AP;
				case BoneTypeEnum.Femur: return BoneImageEnum.Left_Femur_AP;
				case BoneTypeEnum.Tibia: return BoneImageEnum.Left_Tibia_AP;
				case BoneTypeEnum.Ankle: return BoneImageEnum.Left_Ankle_AP;
				case BoneTypeEnum.Forefoot: return BoneImageEnum.Left_Forefoot_AP;
				case BoneTypeEnum.Hindfoot: return BoneImageEnum.Left_Hindfoot_AP;
				default: return '';
			}
		} else { // BoneViewEnum.LT
			switch (this.form()!.boneType) {
				case BoneTypeEnum.LongLeg: return BoneImageEnum.Left_FullLeg_LT;
				case BoneTypeEnum.Femur: return BoneImageEnum.Left_Femur_LT;
				case BoneTypeEnum.Tibia: return BoneImageEnum.Left_Tibia_LT;
				case BoneTypeEnum.Ankle: return BoneImageEnum.Left_Ankle_LT;
				case BoneTypeEnum.Forefoot: return BoneImageEnum.Left_Forefoot_LT;
				case BoneTypeEnum.Hindfoot: return BoneImageEnum.Left_Hindfoot_LT;
				default: return '';
			}
		}
	}

	/**
	 * Change image orientation.
	 */
	changeOrientation(value: boolean): void {
		const currOrientation = this.form()!.orientation;
		const isRot180 = this.form()!.boneView == BoneViewEnum.AP && (this.form()!.boneType == BoneTypeEnum.Forefoot || this.form()!.boneType == BoneTypeEnum.Hindfoot);
		const orientation = isRot180 ? ImageOrientationEnum.Rotate180 : ImageOrientationEnum.Horizontal;
		if (this.isCorrect() == null && !value) {
			this.changed.emit(currOrientation ?? ImageOrientationEnum.Nothing);
		} else if (!currOrientation || currOrientation === ImageOrientationEnum.Nothing) {
			this.flip(orientation);
			this.changed.emit(orientation);
		} else {
			this.flip(orientation);
			this.changed.emit(ImageOrientationEnum.Nothing);
		}
		this.isCorrect.set(value);
	}

	private flip(orientation: ImageOrientationEnum.Rotate180 | ImageOrientationEnum.Horizontal): void {
		if (orientation === ImageOrientationEnum.Rotate180) {
			this.canvasSrv.dispatch('flipBothGarage');
		} else {
			this.canvasSrv.dispatch('flipHorizontalGarage');
		}
	}

}

enum BoneImageEnum {
	Left_Ankle_AP = 'images/bones/ankle_left_AP.png',
	Left_Ankle_LT = 'images/bones/ankle_left_LAT.png',
	Right_Ankle_AP = 'images/bones/ankle_right_AP.png',
	Right_Ankle_LT = 'images/bones/ankle_right_LAT.png',

	Left_Femur_AP = 'images/bones/femur_left_AP.png',
	Left_Femur_LT = 'images/bones/femur_left_LAT.png',
	Right_Femur_AP = 'images/bones/femur_right_AP.png',
	Right_Femur_LT = 'images/bones/femur_right_LAT.png',

	Left_Forefoot_AP = 'images/bones/forefoot_left_AP.png',
	Left_Forefoot_LT = 'images/bones/forefoot_left_LAT.png',
	Right_Forefoot_AP = 'images/bones/forefoot_right_AP.png',
	Right_Forefoot_LT = 'images/bones/forefoot_right_LAT.png',

	Left_FullLeg_AP = 'images/bones/fullleg_left_AP.png',
	Left_FullLeg_LT = 'images/bones/fullleg_left_LAT.png',
	Right_FullLeg_AP = 'images/bones/fullleg_right_AP.png',
	Right_FullLeg_LT = 'images/bones/fullleg_right_LAT.png',

	Left_Hindfoot_AP = 'images/bones/hindfoot_left_AP.png',
	Left_Hindfoot_LT = 'images/bones/hindfoot_left_LAT.png',
	Right_Hindfoot_AP = 'images/bones/hindfoot_right_AP.png',
	Right_Hindfoot_LT = 'images/bones/hindfoot_right_LAT.png',

	Left_Tibia_AP = 'images/bones/tibia_left_AP.png',
	Left_Tibia_LT = 'images/bones/tibia_left_LAT.png',
	Right_Tibia_AP = 'images/bones/tibia_right_AP.png',
	Right_Tibia_LT = 'images/bones/tibia_right_LAT.png'
}
